import { useEffect, useMemo, useState } from 'react'
import { accountsApi } from '../api/accounts'
import useAppStore from '../store/useAppStore'
import { MultiSelect, type SelectOption } from './MultiSelect'
import type { AdEntityFilterField } from './AdEntitySearchSelect'
import Pagination from './Pagination'

const PAGE_SIZE = 20
const DROPDOWN_WIDTH = 360

interface Props {
  field: AdEntityFilterField
  label: string
  value: string
  platform?: 'facebook' | 'tiktok'
  width?: number
  onConfirm: (value: string) => void
}

const splitValues = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)

export function ZeeloolFacebookAdFilterSelect({ field, label, value, platform = 'facebook', width = 220, onConfirm }: Props) {
  const { brandId, siteId } = useAppStore()
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<SelectOption[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const options = useMemo(() => {
    const merged = new Map(items.map((item) => [item.value, item]))
    selected.forEach((selectedValue) => {
      if (!merged.has(selectedValue)) {
        merged.set(selectedValue, { value: selectedValue, label: labels[selectedValue] || '已选广告' })
      }
    })
    return Array.from(merged.values())
  }, [items, labels, selected])

  useEffect(() => {
    if (!open) setSelected(splitValues(value))
  }, [open, value])

  useEffect(() => {
    if (!open || !brandId) return
    const keyword = search.trim()
    if (keyword.length < 2) {
      setLoading(false)
      setError('')
      setItems([])
      setTotal(0)
      return
    }
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setLoading(true)
      setError('')
      try {
        const entity = field === 'campaign_id' || field === 'campaign_name'
          ? 'campaign'
          : field === 'adset_name' ? 'adset' : 'ad'
        const response = await (platform === 'tiktok' ? accountsApi.listTikTokAdEntityOptions : accountsApi.listFacebookAdEntityOptions)({
          brand_id: brandId,
          ...(siteId ? { site_id: siteId } : {}),
          entity,
          search: keyword,
          page,
          size: PAGE_SIZE,
        })
        const nextItems = (response.list || []).map((item) => ({
          value: field === 'campaign_id' ? item.id : item.name,
          label: item.name,
        }))
        const uniqueItems = Array.from(new Map(nextItems.map((item) => [item.value, item])).values())
        if (cancelled) return
        setItems(uniqueItems)
        setTotal(response.total || 0)
        setLabels((current) => ({ ...current, ...Object.fromEntries(uniqueItems.map((item) => [item.value, item.label])) }))
      } catch {
        if (!cancelled) {
          setItems([])
          setTotal(0)
          setError('广告数据加载失败，请重试')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [brandId, field, open, page, platform, search, siteId])

  return (
    <label style={{ display: 'flex', flex: `0 0 ${width}px`, flexDirection: 'column', gap: 4, width, minWidth: width, maxWidth: width }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</span>
      <MultiSelect
        options={options}
        value={selected}
        onChange={setSelected}
        onOpen={() => {
          setSelected(splitValues(value))
          setPage(1)
        }}
        onOpenChange={setOpen}
        searchable
        searchValue={search}
        onSearchChange={(next) => {
          setSearch(next)
          setPage(1)
        }}
        searchPlaceholder="输入名称搜索"
        countLabelFormatter={(count) => `已选 ${count} 项`}
        loading={loading}
        errorText={error}
        emptyText={search.trim().length < 2 ? '请输入至少 2 个字符搜索' : '暂无可用广告数据'}
        style={{ width }}
        dropdownWidth={Math.max(width, DROPDOWN_WIDTH)}
        dropdownFooter={(close) => (
          <>
            <Pagination className="pagination-compact" page={page} totalPages={totalPages} onChange={setPage} total={`共 ${total} 条`} disabled={loading} style={{ padding: '6px 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--border)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => { setSelected(splitValues(value)); close() }}>取消</button>
              <button type="button" className="btn btn-primary" onClick={() => { onConfirm(selected.join(',')); close() }}>确定</button>
            </div>
          </>
        )}
      />
    </label>
  )
}
